/**
 * Vincent B1026 (Eric 2026-02-26 SPEC STRICK PROD) — Menu déroulant unifié
 * « Reprendre... » qui remplace les 3 boutons Play (Relancer / Continuer
 * sans diarisation / Lancer l'extraction) affichés côte à côte dans la
 * File d'attente.
 *
 * Vincent B1028 (Eric 2026-02-26) — Refactor : le moteur commun de
 * rendu et de comportement (click-outside, Escape, ARIA, testids) a été
 * extrait dans `ActionMenu.js`. Ce composant ne gère plus que la logique
 * métier propre à la « reprise » (mapping des items selon pipeline_status).
 *
 * B1122 (Eric 2026-09-08) — Deux reprises manquaient au menu : « Recommencer
 * la transcription » et « Re-transcrire sans dédoublonnage ». Elles
 * n'existaient que dans l'Explorateur et dans la fenêtre de réglages Whisper,
 * donc invisibles depuis la file d'attente — là où on constate justement
 * qu'une transcription a mal tourné.
 */
import { ArrowsClockwise, Play, TextAa } from "@phosphor-icons/react";

import { useLang } from "../../contexts/LanguageContext";
import ActionMenu from "./ActionMenu";


export default function ResumeMenu({ video, busy, onRestart, onResumeNoDiar, onRerunWhisper, onRerunWhisperNoDedup }) {
  const { tr } = useLang();
  const status = video?.pipeline_status;
  const items = [];

  // 1. Relancer (toujours disponible pour canRestart — délégation au parent).
  items.push({
    id: "restart",
    label: tr("Relancer le pipeline", "Relaunch the pipeline"),
    desc: tr(
      "Reprend au dernier checkpoint, avec diarisation complète.",
      "Resumes at the last checkpoint, with full diarization.",
    ),
    onClick: onRestart,
    busyKey: "resume",
    icon: Play,
  });

  // 2. Continuer sans diarisation (Whisper OK, diarisation en timeout).
  if (status === "paused_awaiting_user") {
    items.push({
      id: "resume-no-diar",
      label: tr("Continuer sans diarisation", "Continue without diarization"),
      desc: tr(
        "Reprend en assumant 1 seul locuteur (pour débloquer un timeout de diarisation).",
        "Resumes assuming a single speaker (to get past a diarization timeout).",
      ),
      onClick: onResumeNoDiar,
      busyKey: "resume-no-diar",
      icon: Play,
    });
  }

  // 3. Lancer l'extraction (Whisper+Fireflies fusionnés, il reste à générer Q&R/Scripts/…).
  // ⚠️ Tâche 1493 (Eric 2026-06) — RETIRÉE d'ici. Cette entrée appelait
  // `resume-after-fusion`, qui REFAIT la fusion quand la vidéo est en
  // `paused_pending_fusion` : son libellé promettait l'extraction et
  // facturait une fusion. Le menu « Continuer (connaissances) » monté à côté
  // (`MenuContinuerPipeline`) nomme chaque étape et dit si elle est déjà
  // faite. Garder les deux aurait laissé deux chemins divergents pour le
  // même geste.

  // 4 + 5. B1122 — Reprises Whisper : disponibles dès que le pipeline n'est
  // plus en cours d'exécution (une re-transcription pendant le run
  // écraserait le travail en train de se faire).
  if (onRerunWhisper && status !== "processing" && status !== "pending") {
    items.push({
      id: "rerun-whisper",
      label: tr("Recommencer la transcription", "Start the transcript over"),
      desc: tr(
        "Efface la transcription Whisper et la refait depuis le début (appel facturé).",
        "Erases the Whisper transcript and redoes it from scratch (billed call).",
      ),
      onClick: onRerunWhisper,
      busyKey: "rerun-whisper",
      icon: ArrowsClockwise,
    });
    items.push({
      id: "rerun-whisper-no-dedup",
      label: tr("Re-transcrire sans dédoublonnage", "Re-transcribe without de-duplication"),
      desc: tr(
        "Même chose, mais sans le filtre anti-hallucination : à utiliser quand la transcription revient anormalement courte.",
        "Same thing, without the anti-hallucination filter: use it when the transcript comes back unusually short.",
      ),
      onClick: onRerunWhisperNoDedup,
      busyKey: "rerun-whisper-no-dedup",
      icon: TextAa,
    });
  }

  return (
    <ActionMenu
      video={video}
      busy={busy}
      items={items}
      triggerLabel={tr("Reprendre", "Resume")}
      triggerIcon={Play}
      triggerClass="bg-blue-600 text-white hover:bg-blue-500"
      testidPrefix="queue-resume-menu"
    />
  );
}
